import { useState } from 'react';
import { Lock, CreditCard, Calendar, Shield, AlertCircle, CheckCircle, X, ChevronLeft, Apple } from 'lucide-react';
import { BookingData } from './BookingFlow';
import petsoLogo from '../../assets/4c0bdef843fbe05c3bb4b14b38ec794cce2e65c9.png';
import petsoPaw from '../../assets/78751f73824316be9d04898afd4169ed5ee341b1.png';

interface CardPaymentCheckoutProps {
  bookingData: BookingData;
  totalAmount: number;
  onSuccess: () => void;
  onCancel: () => void;
  onBack: () => void;
}

export function CardPaymentCheckout({ 
  bookingData, 
  totalAmount, 
  onSuccess, 
  onCancel,
  onBack 
}: CardPaymentCheckoutProps) {
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [saveCard, setSaveCard] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '');
    const chunks = cleaned.match(/.{1,4}/g);
    return chunks ? chunks.join(' ') : cleaned;
  };

  // Format expiry date as MM/YY
  const formatExpiryDate = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');
    if (value.length <= 16 && /^\d*$/.test(value)) {
      setCardNumber(formatCardNumber(value));
    }
  };

  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 4) {
      setExpiryDate(formatExpiryDate(value));
    }
  };

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 4 && /^\d*$/.test(value)) {
      setCvv(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsProcessing(true);

    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Mock validation
    if (cardNumber.replace(/\s/g, '').length !== 16) {
      setError('Invalid card number');
      setIsProcessing(false);
      return;
    }

    if (!expiryDate.match(/^\d{2}\/\d{2}$/)) {
      setError('Invalid expiry date');
      setIsProcessing(false);
      return;
    }

    if (cvv.length < 3) {
      setError('Invalid CVV');
      setIsProcessing(false);
      return;
    }

    // Simulate success
    onSuccess();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F7F8FA' }}>
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 transition-colors"
            style={{ 
              color: '#594BFF', 
              fontFamily: 'Sora, sans-serif', 
              fontWeight: 600,
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2DFEA',
              borderRadius: '12px'
            }}
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" style={{ color: '#464452' }} />
          </button>
        </div>

        <div className="p-8" style={{ backgroundColor: '#FFFFFF', borderRadius: '24px', border: '1px solid #E2DFEA', boxShadow: '0 12px 32px rgba(89, 75, 255, 0.08)' }}>
          {/* Secure Checkout Header */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="flex items-center gap-2">
              <img 
                src={petsoLogo} 
                alt="Petso" 
                className="h-8"
                style={{ objectFit: 'contain' }}
              />
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5" style={{ backgroundColor: '#D1FAE5', borderRadius: '8px' }}>
              <Lock className="w-4 h-4" style={{ color: '#10B981' }} />
              <span style={{ fontSize: '0.875rem', fontFamily: 'Sora, sans-serif', fontWeight: 600, color: '#10B981' }}>
                Secure Checkout
              </span>
            </div>
          </div>

          <h2 style={{ fontSize: '1.75rem', fontFamily: 'Sora, sans-serif', fontWeight: 700, color: '#2D1F7B', marginBottom: '0.5rem', textAlign: 'center' }}>
            Complete Your Payment
          </h2>
          <p style={{ fontFamily: 'Inter, sans-serif', color: '#464452', textAlign: 'center', marginBottom: '2rem' }}>
            Enter your card details to confirm your booking
          </p>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 flex items-start gap-3" style={{ backgroundColor: '#FEE2E2', border: '1px solid #EF4444', borderRadius: '12px' }}>
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#EF4444' }} />
              <div style={{ fontSize: '0.875rem', color: '#991B1B', fontFamily: 'Inter, sans-serif' }}>
                {error}
              </div>
            </div>
          )}

          {/* Payment Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Quick Pay Options */}
            <div>
              <label className="block mb-3" style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#2D1F7B' }}>
                Express Checkout
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 px-4 py-3 transition-all"
                  style={{
                    backgroundColor: '#000000',
                    color: '#FFFFFF',
                    borderRadius: '12px',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 600
                  }}
                >
                  <Apple className="w-5 h-5" />
                  Apple Pay
                </button>
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 px-4 py-3 transition-all"
                  style={{
                    backgroundColor: '#FFFFFF',
                    border: '2px solid #E2DFEA',
                    color: '#2D1F7B',
                    borderRadius: '12px',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 600
                  }}
                >
                  G Pay
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" style={{ borderColor: '#E2DFEA' }}></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4" style={{ backgroundColor: '#FFFFFF', color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                  Or pay with card
                </span>
              </div>
            </div>

            {/* Card Number */}
            <div>
              <label className="block mb-2" style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#2D1F7B' }}>
                Card Number
              </label>
              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#464452', opacity: 0.5 }} />
                <input
                  type="text"
                  value={cardNumber}
                  onChange={handleCardNumberChange}
                  placeholder="1234 5678 9012 3456"
                  required
                  className="w-full pl-10 pr-4 py-3 transition-all"
                  style={{
                    backgroundColor: '#F7F8FA',
                    border: '2px solid #E2DFEA',
                    borderRadius: '12px',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '1rem',
                    color: '#2D1F7B'
                  }}
                />
                {/* Card Brand Icons */}
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-1">
                  <div className="w-8 h-6 bg-gray-200 rounded flex items-center justify-center text-xs" style={{ fontWeight: 600 }}>
                    💳
                  </div>
                </div>
              </div>
            </div>

            {/* Expiry & CVV */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-2" style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#2D1F7B' }}>
                  Expiry Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: '#464452', opacity: 0.5 }} />
                  <input
                    type="text"
                    value={expiryDate}
                    onChange={handleExpiryDateChange}
                    placeholder="MM/YY"
                    required
                    className="w-full pl-10 pr-4 py-3 transition-all"
                    style={{
                      backgroundColor: '#F7F8FA',
                      border: '2px solid #E2DFEA',
                      borderRadius: '12px',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '1rem',
                      color: '#2D1F7B'
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="block mb-2" style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#2D1F7B' }}>
                  CVV
                </label>
                <input
                  type="text"
                  value={cvv}
                  onChange={handleCvvChange}
                  placeholder="123"
                  required
                  className="w-full px-4 py-3 transition-all"
                  style={{
                    backgroundColor: '#F7F8FA',
                    border: '2px solid #E2DFEA',
                    borderRadius: '12px',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '1rem',
                    color: '#2D1F7B'
                  }}
                />
              </div>
            </div>

            {/* Cardholder Name */}
            <div>
              <label className="block mb-2" style={{ fontFamily: 'Sora, sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#2D1F7B' }}>
                Cardholder Name
              </label>
              <input
                type="text"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                placeholder="John Doe"
                required
                className="w-full px-4 py-3 transition-all"
                style={{
                  backgroundColor: '#F7F8FA',
                  border: '2px solid #E2DFEA',
                  borderRadius: '12px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '1rem',
                  color: '#2D1F7B'
                }}
              />
            </div>

            {/* Save Card */}
            <div className="flex items-center gap-3 p-4" style={{ backgroundColor: '#F7F8FA', borderRadius: '12px' }}>
              <input
                type="checkbox"
                id="saveCard"
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
                className="w-4 h-4"
                style={{ accentColor: '#594BFF' }}
              />
              <label htmlFor="saveCard" className="flex-1 cursor-pointer" style={{ fontSize: '0.875rem', fontFamily: 'Inter, sans-serif', color: '#2D1F7B' }}>
                Save card for future bookings (optional)
              </label>
            </div>

            {/* Amount Summary */}
            <div className="p-6" style={{ backgroundColor: '#F7F8FA', borderRadius: '16px', border: '2px solid #E2DFEA' }}>
              <div className="flex items-center justify-between mb-2">
                <span style={{ fontFamily: 'Inter, sans-serif', color: '#464452' }}>
                  Appointment Total
                </span>
                <span style={{ fontSize: totalAmount > 0 ? '1.75rem' : '0.875rem', fontFamily: 'Sora, sans-serif', fontWeight: 700, color: '#2D1F7B' }}>
                  {totalAmount > 0 ? `$${totalAmount.toFixed(2)}` : 'Confirmed by professional'}
                </span>
              </div>
              <p style={{ fontSize: '0.75rem', color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                You'll be charged immediately
              </p>
            </div>

            {/* Security Notice */}
            <div className="p-4 flex items-start gap-3" style={{ backgroundColor: '#F0EEFF', border: '1px solid #594BFF', borderRadius: '12px' }}>
              <Shield className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#594BFF' }} />
              <div style={{ fontSize: '0.75rem', color: '#2D1F7B', fontFamily: 'Inter, sans-serif', lineHeight: 1.6 }}>
                <strong>Your payment is protected.</strong> All transactions are encrypted end-to-end. 
                Petso uses Stripe for secure, PCI-compliant payment processing. We never store your card details.
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isProcessing}
              className="w-full flex items-center justify-center gap-2 py-4 text-white transition-all"
              style={{
                background: isProcessing ? '#9CA3AF' : 'linear-gradient(135deg, #594BFF 0%, #8B7FFF 100%)',
                borderRadius: '16px',
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
                fontSize: '1.125rem',
                cursor: isProcessing ? 'not-allowed' : 'pointer'
              }}
            >
              {isProcessing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing payment...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  {totalAmount > 0 ? `Pay $${totalAmount.toFixed(2)}` : 'Confirm Booking'}
                </>
              )}
            </button>

            {/* Trust Badges */}
            <div className="flex items-center justify-center gap-6 pt-4">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" style={{ color: '#10B981' }} />
                <span style={{ fontSize: '0.75rem', color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                  Stripe Secured
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4" style={{ color: '#10B981' }} />
                <span style={{ fontSize: '0.75rem', color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                  256-bit SSL
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" style={{ color: '#10B981' }} />
                <span style={{ fontSize: '0.75rem', color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                  PCI Compliant
                </span>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}